import clsx from "clsx";
import { ChevronRight, Trash } from "lucide-react";
import React, { useState } from "react";

import { Badge } from "@/design-system/components/Badge/Badge";
import { IconButton } from "@/design-system/components/IconButton/IconButton";
import { toast } from "@/design-system/components/Toast/Toast";
import { TransformationDeleteModal } from "@/transforms/components/TransformationDeleteModal";
import {
  TransformationSetDescription,
  TransformationSetEditor,
} from "@/transforms/components/TransformsTable";
import { TransformDefinitionSet } from "@/transforms/transformationDefinition";
import { deleteTransformationSet } from "@/transforms/transformations";

type TransformationSetItemProps = {
  transformationSet: TransformDefinitionSet;
  isOpen: boolean;
  onToggle: () => void;
  isEditable: boolean;
  isServerSideOnly: boolean;
  onChange: () => void;
};

export function TransformationSetItem({
  transformationSet,
  isOpen,
  onToggle,
  isEditable,
  isServerSideOnly,
  onChange,
}: TransformationSetItemProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const IntentionIcon = transformationSet.icon();

  async function confirmDelete() {
    setConfirmingDelete(false);
    try {
      await deleteTransformationSet(transformationSet.id);
      onChange();
    } catch (error) {
      toast.error("Couldn’t delete the transformation set. Try again.");
      console.error(error);
    }
  }

  return (
    <li className="flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <button
            type="button"
            aria-expanded={isOpen}
            onClick={onToggle}
            className="text-label-sm text-textPrimary flex cursor-pointer items-center gap-2 text-left"
          >
            <ChevronRight
              aria-hidden
              className={clsx(
                "text-iconDefault size-icon-md shrink-0 transition-transform",
                isOpen && "rotate-90",
              )}
            />
            {transformationSet.eventDefNameDisplay()}
          </button>
          <div className="pl-6">
            {transformationSet.transformations.length === 0 ? (
              <span className="text-body-xs text-textTertiary">
                No transformations yet
              </span>
            ) : (
              <TransformationSetDescription
                transformationSet={transformationSet}
              />
            )}
          </div>
        </div>
        <Badge
          variant="success"
          className="whitespace-nowrap"
          text={
            <>
              <IntentionIcon className="size-icon-md mr-1 fill-current" />
              {transformationSet.intentionDisplay()}
            </>
          }
        />
        {isEditable && (
          <IconButton
            aria-label="Delete transformation set"
            tooltipContent="Delete"
            onClick={() => setConfirmingDelete(true)}
          >
            <Trash className="size-icon-md" aria-hidden />
          </IconButton>
        )}
      </div>
      {isOpen && (
        <div className="border-borderSubtle border-t p-4">
          <TransformationSetEditor
            layout="stacked"
            transformationSet={transformationSet}
            isServerSideOnly={isServerSideOnly}
            isEditable={isEditable}
            reloadAllTransforms={onChange}
          />
        </div>
      )}
      <TransformationDeleteModal
        header="Delete this transformation set?"
        isOpen={confirmingDelete}
        onConfirm={confirmDelete}
        onClose={() => setConfirmingDelete(false)}
      />
    </li>
  );
}
